import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRM, PROCESS_TIMES, loadProcessTimes } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, ArrowLeft, Upload } from "lucide-react";
import { ProductImage } from "@/components/ProductImage";
import { DeliveryPicker } from "@/components/ProductOptions";
import { OrderItemsEditor, newItem, itemPayload } from "@/components/OrderItems";

export default function AgentNewOrder() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [artwork, setArtwork] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");
  const [form, setForm] = useState({ delivery_method: "", customer_name: "", customer_phone: "", process_time: "Normal Job", notes: "" });
  const [items, setItems] = useState([newItem(false)]);
  const [methods, setMethods] = useState([]);
  const [pts, setPts] = useState(PROCESS_TIMES);
  const [quote, setQuote] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    loadProcessTimes().then((p) => setPts([...p]));
    api.get("/delivery-methods").then((r) => { setMethods(r.data); if (r.data[0]) setForm((f) => ({ ...f, delivery_method: f.delivery_method || r.data[0].name })); });
  }, []);

  const pt = pts.find((p) => p.value === form.process_time) || pts[0] || { value: "" };
  const noneSelected = !items.some((it) => it.product_id);
  const pickFromGallery = (pid) => setItems((cur) => { const idx = cur.findIndex((it) => !it.product_id); const it = { ...newItem(false), product_id: pid }; return idx === -1 ? [...cur, it] : cur.map((x, i) => (i === idx ? { ...x, product_id: pid, specs: {}, finishing_sel: [] } : x)); });

  const submit = async (e) => {
    e.preventDefault();
    const ready = items.filter((it) => it.product_id);
    if (!ready.length) return toast.error("Please select at least one product");
    if (!quote?.ok) return toast.error(quote?.errors?.[0] || "Complete the product specifications");
    setSaving(true);
    try {
      const payload = { ...form, items: ready.map(itemPayload), quantity: quote.quantity };
      const { data } = await api.post("/agent/jobs", payload);
      const uploads = [...ready.flatMap((it, i) => (it.files || []).map((f) => ({ f, i }))), ...artwork.map((f) => ({ f, i: null }))];
      let done = 0, failed = 0;
      for (const { f, i } of uploads) {
        const fd = new FormData();
        fd.append("file", f);
        if (i !== null) fd.append("item_index", String(i));
        setProgress(`Uploading ${++done}/${uploads.length}: ${f.name}`);
        try { await api.post(`/agent/jobs/${data.id}/artworks`, fd); } catch { failed++; }
      }
      toast.success(`Order ${data.job_number} submitted${uploads.length ? ` • ${uploads.length - failed}/${uploads.length} file(s) uploaded` : ""}`);
      if (failed) toast.error(`${failed} file(s) failed to upload — you can re-upload from the order page`);
      nav(`/agent/jobs/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit order");
    } finally { setSaving(false); setProgress(""); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="agent-new-order-page">
      <Button variant="ghost" onClick={() => nav("/agent")}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">Agent Order</div>
        <h1 className="text-3xl font-display font-extrabold tracking-tight">Submit New Order</h1>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Card className="p-5 space-y-4">
          <div>
            <h2 className="font-display font-bold">Products</h2>
            <p className="text-xs text-muted-foreground">You can add several products to one order. Prices shown are your agent tier prices.</p>
          </div>
          {noneSelected && products.some((p) => p.image) && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="agent-product-gallery">
              {products.map((p) => (
                <button type="button" key={p.id} onClick={() => pickFromGallery(p.id)} className="text-left rounded-lg border p-1.5 hover:border-amber-400 hover:shadow transition-all" data-testid={`agent-gallery-${p.id}`}>
                  <ProductImage product={p} className="w-full aspect-square" />
                  <div className="text-[11px] font-semibold mt-1 truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">from {formatRM(p.agent_price)}</div>
                </button>
              ))}
            </div>
          )}
          <OrderItemsEditor items={items} onChange={setItems} products={products} quoteUrl="/agent/quote" processTime={form.process_time} deliveryMethod={form.delivery_method} onQuote={setQuote}
            accent="bg-amber-400" testid="agent-order-item" allowFiles priceLabel={(p) => `from ${formatRM(p.agent_price)}/${p.unit}`} />
          <div>
            <Label>Delivery / Pickup *</Label>
            <DeliveryPicker methods={methods} value={form.delivery_method} onChange={(v) => set("delivery_method", v)} testid="agent-order-delivery" />
          </div>
          <div className="text-xs text-muted-foreground" data-testid="agent-order-pt-info">Process time: {pt.value}{pt.turnaround ? ` (${pt.turnaround})` : ""}</div>
        </Card>

        <Card className="p-5 space-y-4">
          <h2 className="font-display font-bold">End Customer & Artwork</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Customer Name *</Label><Input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="agent-order-customer" /></div>
            <div><Label>Customer Phone No.</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="agent-order-phone" /></div>
            <div>
              <Label>Process Time (Excluding Postage Duration) *</Label>
              <Select value={form.process_time} onValueChange={(v) => set("process_time", v)}>
                <SelectTrigger data-testid="agent-order-process-time"><SelectValue /></SelectTrigger>
                <SelectContent>{pts.map((p) => <SelectItem key={p.value} value={p.value}>{p.value} — {p.turnaround}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional Artwork / Files for the whole order (multiple)</Label>
              <label className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                <Upload className="w-4 h-4" />
                <span className="truncate">{artwork.length ? `${artwork.length} file(s) selected — add more` : "Choose file(s)"}</span>
                <input type="file" hidden multiple accept="image/*,.pdf,.ai,.psd,.cdr" onChange={(e) => { setArtwork((a) => [...a, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="agent-order-artwork" />
              </label>
              {artwork.length > 0 && <ul className="text-xs mt-1 space-y-0.5" data-testid="agent-order-artwork-list">{artwork.map((f, i) => <li key={i} className="flex justify-between gap-2 bg-muted/40 rounded px-2 py-1"><span className="truncate">{f.name}</span><button type="button" onClick={() => setArtwork((a) => a.filter((_, j) => j !== i))} className="text-rose-600 text-xs">remove</button></li>)}</ul>}
            </div>
            <div className="md:col-span-2"><Label>Notes / Instructions</Label><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Colours, special instructions, delivery address..." data-testid="agent-order-notes" /></div>
          </div>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => nav("/agent")}>Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="agent-order-submit">
            <Send className="w-4 h-4 mr-1" /> {saving ? (progress || "Sending...") : "Submit Order"}
          </Button>
        </div>
      </form>
    </div>
  );
}
